import { type MouseEvent, type PointerEvent as ReactPointerEvent, useCallback, useRef, useState } from 'react';

function isPointerInside(event: ReactPointerEvent<HTMLElement>) {
    const { left, right, top, bottom } = event.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = event;
    return clientX >= left && clientX <= right && clientY >= top && clientY <= bottom;
}

type UseLiquidGlassPressOptions = {
    disabled?: boolean;
    onActivate?: () => void;
    onPressedChange?: (pressed: boolean) => void;
    preventDefault?: boolean;
};

export function useLiquidGlassPress(options: UseLiquidGlassPressOptions = {}) {
    const { disabled = false, onActivate, onPressedChange, preventDefault = false } = options;
    const pointerDownRef = useRef(false);
    const activeRef = useRef(false);
    const suppressClickRef = useRef(false);
    const [pressed, setPressed] = useState(false);
    const updatePressed = useCallback(
        (value: boolean) => {
            setPressed(value);
            onPressedChange?.(value);
        },
        [onPressedChange],
    );

    const finishPointer = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            pointerDownRef.current = false;
            activeRef.current = false;
            updatePressed(false);
            if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
            }
        },
        [updatePressed],
    );

    const onPointerDown = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (disabled) return;
            pointerDownRef.current = true;
            activeRef.current = true;
            suppressClickRef.current = false;
            updatePressed(true);
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        [disabled, updatePressed],
    );

    const onPointerMove = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (!pointerDownRef.current || disabled) return;
            const inside = isPointerInside(event);
            activeRef.current = inside;
            updatePressed(inside);
        },
        [disabled, updatePressed],
    );

    const onPointerUp = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (!pointerDownRef.current) return;
            if (!activeRef.current || !isPointerInside(event)) suppressClickRef.current = true;
            finishPointer(event);
        },
        [finishPointer],
    );

    const onPointerCancel = useCallback(
        (event: ReactPointerEvent<HTMLElement>) => {
            if (!pointerDownRef.current) return;
            suppressClickRef.current = true;
            finishPointer(event);
        },
        [finishPointer],
    );

    const onClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            if (suppressClickRef.current) {
                suppressClickRef.current = false;
                event.preventDefault();
                return;
            }
            if (disabled) {
                event.preventDefault();
                return;
            }
            onActivate?.();
            if (preventDefault) event.preventDefault();
        },
        [disabled, onActivate, preventDefault],
    );

    return {
        pressed: pressed && !disabled,
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onPointerCancel,
        onClick,
    };
}
